import { CircularProgress } from '@ketch-com/deck'
import { Box, Typography, styled } from '@mui/material'
import React, { useContext, useMemo } from 'react'
import { ExperiencePreviewContext } from '../context/ExperiencePreviewContext'
import { PreviewMessageType } from '@ketch-sdk/ketch-types'

export type HeaderData = {
  experienceType: PreviewMessageType
  language: string
  jurisdiction: string
}

const PreviewMessageTypeLabels: Record<PreviewMessageType, string> = {
  [PreviewMessageType.ShowBanner]: 'Banner',
  [PreviewMessageType.ShowModal]: 'Modal',
  [PreviewMessageType.ShowPreference]: 'Preference',
  [PreviewMessageType.ShowConsentGate]: 'Consent Gate',
  [PreviewMessageType.LoadConfig]: '',
  [PreviewMessageType.SwitchPreferencePage]: '',
}

const PreviewHeaderBox = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  padding: '12px 16px',
  boxShadow: '0px 4px 10px 0px rgba(7, 26, 36, 0.10);',
  zIndex: 3,
})

const PreviewHeaderLeftBox = styled(Box)({
  display: 'flex',
  gap: '32px',
  alignItems: 'flex-end',
})

const PreviewHeaderRightBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
})

const HeaderDataBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
})

interface DirectConfigPreviewHeaderProps {
  isLoading: boolean
  headerData?: HeaderData
}

export const DirectConfigPreviewHeader: React.FC<DirectConfigPreviewHeaderProps> = ({
  isLoading = false,
  headerData,
}) => {
  const { languageOptions } = useContext(ExperiencePreviewContext)

  const language = useMemo(() => {
    return (
      languageOptions?.find(option => option.language.code === headerData?.language)?.language.englishName ||
      headerData?.language ||
      'None'
    )
  }, [languageOptions, headerData?.language])

  return (
    <PreviewHeaderBox>
      <PreviewHeaderLeftBox>
        <HeaderDataBox>
          <Typography variant="smallLabel">Experience Type</Typography>
          <Typography variant="smallBody">
            {PreviewMessageTypeLabels[headerData?.experienceType || PreviewMessageType.ShowBanner]}
          </Typography>
        </HeaderDataBox>

        <HeaderDataBox>
          <Typography variant="smallLabel">Language</Typography>
          <Typography variant="smallBody">{language}</Typography>
        </HeaderDataBox>

        {headerData?.jurisdiction && (
          <HeaderDataBox>
            <Typography variant="smallLabel">Jurisdiction</Typography>
            <Typography variant="smallBody">{headerData.jurisdiction}</Typography>
          </HeaderDataBox>
        )}
      </PreviewHeaderLeftBox>

      <PreviewHeaderRightBox>
        {isLoading && <CircularProgress label={<Typography variant={'label'}>Preview Loading</Typography>} />}
      </PreviewHeaderRightBox>
    </PreviewHeaderBox>
  )
}
